import { flatMap } from 'lodash-es';
import type { Observable } from 'rxjs';
import { combineLatestWith, EMPTY, map, switchMap } from 'rxjs';
import type { VperBucket } from '@/graphql/types';
import { GraphType } from '../../enums';
import { DUST_GRAPH_DATA_TYPES } from '../constants';
import { BarGraphType, DotGraphType, LineGraphType, StepGraphType } from '../types';
import {
    BaseGraphElement,
    BaseGraphElementsProvider,
    filterElementsOnAxesWithLegend,
    sortAxis,
    valueNotNull,
} from './base-provider';
import type { SampleRecord } from '../graph';
import type { DataSet } from '../dataset';
import type { LegendData } from '../legend-helpers';
import { availableAxis, type AxisType } from '../axis';

type SamplesDataType = keyof Pick<SampleRecord, 'atop' | 'vtop' | 'fdom' | 'veff' | 'vdv'>;

export class SampleAxisGraphElement extends BaseGraphElement {
    dataType: SamplesDataType;

    axis: AxisType;

    constructor(record: SampleRecord, dataType: SamplesDataType, axis: AxisType) {
        super(record);

        this.dataType = dataType;
        this.axis = axis;
    }

    get timestamp() {
        return (this.record as SampleRecord)[this.dataType]!.timestamp as number;
    }

    getValueForDataType(dataType: SamplesDataType): number | null {
        const data = (this.record as SampleRecord)[dataType];
        if (!data) return null;
        const value = data[this.axis as keyof typeof data] as number;

        if (value !== undefined) {
            return value;
        }
        return null;
    }

    get value(): number | null {
        return this.getValueForDataType(this.dataType);
    }

    static createCollectionFromRecords(
        records: SampleRecord[],
        dataType: SamplesDataType
    ): SampleAxisGraphElement[] {
        return flatMap(records, (record) =>
            availableAxis
                .map((axis) => new this(record, dataType, axis))
                .filter(valueNotNull)
                .sort(sortAxis)
        );
    }

    getVperPeriodForTimestamp(timestamp: number, dataSet: DataSet): VperBucket | null {
        return (
            dataSet.vperPeriods.find((period) => {
                const { timespan } = period;
                return timespan!.from <= timestamp && timestamp <= timespan!.to;
            }) || null
        );
    }
}

/**
 * Observables flow diagram: https://miro.com/app/board/uXjVPSSWJQ0=/
 */
export class SamplesGraphElementsProvider extends BaseGraphElementsProvider {
    static getGraphElementClass(): typeof SampleAxisGraphElement {
        return SampleAxisGraphElement;
    }

    static createGraphElementsObservable(
        dataSet: DataSet,
        legendData: LegendData
    ): Observable<SampleAxisGraphElement[]> {
        return dataSet.samples$.pipe(
            combineLatestWith(dataSet.dataType$),
            map(([records, dataType]) =>
                this.getGraphElementClass().createCollectionFromRecords(
                    records,
                    dataType.toLowerCase() as SamplesDataType
                )
            ),
            filterElementsOnAxesWithLegend(legendData)
        );
    }

    // Extra class for the renderer, useful for D3 to distinguish DOM
    // elements manager by different renderers.
    getD3IdentifierCssClass(): string {
        return 'not-dust';
    }

    init(): void {
        const numberOfRecords = 365;
        const rectWidth = this.graph.graphWidth / numberOfRecords;

        const graphTypes = {
            [GraphType.Line]: [LineGraphType, []],
            [GraphType.Step]: [StepGraphType, []],
            [GraphType.Bar]: [BarGraphType, [rectWidth]],
            [GraphType.Dot]: [DotGraphType, [rectWidth]],
        } as const;

        const renderer$ = this.graph.graphType$.pipe(
            map((graphType) => graphType.toUpperCase() as GraphType),
            switchMap((graphType) => {
                if (!Object.keys(graphTypes).includes(graphType)) {
                    // eslint-disable-next-line no-console
                    console.error(`No renderer for graphType ${graphType}`);
                    return EMPTY;
                }

                const [Renderer, args] = graphTypes[graphType];
                return Renderer.createRenderObservable(
                    this.graphElementPositions$,
                    this.graph,
                    this.getD3IdentifierCssClass(),
                    // We know args match the constructor parameters.
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    ...args
                );
            })
        );

        this.subscription.add(renderer$.subscribe());
    }

    createIsActiveObservable(): Observable<boolean> {
        return this.dataSet.dataType$.pipe(
            map((dataType) => !DUST_GRAPH_DATA_TYPES.includes(dataType))
        );
    }
}
